import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../Common/Header';
import { api } from '../../services/api';
import './PollingPercentage.css';

const PollingPercentage = () => {
  const { t } = useTranslation();
  
  // ============ STATE MANAGEMENT ============
  const [selectedPhase, setSelectedPhase] = useState(1);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districtStats, setDistrictStats] = useState([]);
  const [constituencyData, setConstituencyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('percentage');
  const [searchTerm, setSearchTerm] = useState('');

  // ============ EFFECTS ============
  
  useEffect(() => {
    loadDistrictStats();
  }, [selectedPhase]);

  // ============ HELPER: ENSURE ARRAY ============
  
  const ensureArray = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      return data.data || data.districts || data.constituencies || data.items || [];
    }
    return [];
  };

  // ============ API CALLS ============
  
  const loadDistrictStats = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedDistrict(null);
      setConstituencyData([]);
      setSearchTerm('');

      console.log(`📊 Loading district stats for phase ${selectedPhase}`);
      
      const response = await api.getDistrictPollingStats(selectedPhase);
      console.log('✅ District stats response:', response);
      
      const data = ensureArray(response);
      console.log('✅ Extracted districts array:', data);
      
      setDistrictStats(data);
    } catch (err) {
      console.error('❌ Error loading district stats:', err);
      setError(t('polling.messages.error'));
      setDistrictStats([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConstituencyData = async (districtName) => {
    try {
      setLoading(true);
      setError(null);
      setSearchTerm('');

      console.log(`🏛️ Loading constituencies for district: ${districtName}`);
      
      const response = await api.getPollDataByDistrict(districtName, selectedPhase);
      console.log('✅ Constituency data response:', response);
      
      const data = ensureArray(response);
      console.log('✅ Extracted constituency array:', data);
      
      setConstituencyData(data);
      setSelectedDistrict(districtName);
    } catch (err) {
      console.error('❌ Error loading constituency data:', err);
      setError(t('polling.messages.errorLoadingConstituencies'));
      setConstituencyData([]);
    } finally {
      setLoading(false);
    }
  };

  // ============ HELPER FUNCTIONS ============
  
  const getPercentageColor = (percentage) => {
    const pct = parseFloat(percentage) || 0;
    if (pct >= 65) return '#10b981';
    if (pct >= 60) return '#3b82f6';
    if (pct >= 55) return '#f59e0b';
    return '#ef4444';
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getPhaseDate = (phase) => {
    const dates = {
      1: 'Nov 06', 2: 'Nov 11'
    };
    return dates[phase] || 'TBA';
  };

  // ============ SORTING & FILTERING ============
  
  const getSortedDistrictData = () => {
    let data = Array.isArray(districtStats) ? [...districtStats] : [];
    
    if (searchTerm) {
      data = data.filter(d =>
        d?.district?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return data.sort((a, b) => {
      const aVal = (sortBy === 'percentage' ? parseFloat(a?.averagePercentage) || 0 :
                    sortBy === 'male' ? parseFloat(a?.averageMalePercentage) || 0 :
                    parseFloat(a?.averageFemalePercentage) || 0);
      
      const bVal = (sortBy === 'percentage' ? parseFloat(b?.averagePercentage) || 0 :
                    sortBy === 'male' ? parseFloat(b?.averageMalePercentage) || 0 :
                    parseFloat(b?.averageFemalePercentage) || 0);
      
      return bVal - aVal;
    });
  };

  const getSortedConstituencyData = () => {
    let data = Array.isArray(constituencyData) ? [...constituencyData] : [];
    
    console.log('🔍 Processing constituency data:', { isArray: Array.isArray(data), length: data.length });
    
    if (searchTerm) {
      data = data.filter(c =>
        c?.constituency?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return data.sort((a, b) => {
      const aVal = (sortBy === 'percentage' ? parseFloat(a?.overallPercentage) || 0 :
                    sortBy === 'male' ? parseFloat(a?.malePercentage) || 0 :
                    parseFloat(a?.femalePercentage) || 0);
      
      const bVal = (sortBy === 'percentage' ? parseFloat(b?.overallPercentage) || 0 :
                    sortBy === 'male' ? parseFloat(b?.malePercentage) || 0 :
                    parseFloat(b?.femalePercentage) || 0);
      
      return bVal - aVal;
    });
  };

  const sortedDistrictData = getSortedDistrictData();
  const sortedConstituencyData = getSortedConstituencyData();

  // ============ STATISTICS ============
  
  const getConstituencySummary = () => {
    const data = Array.isArray(constituencyData) ? constituencyData : [];
    
    if (data.length === 0) {
      return { total: 0, avg: '0.00', highest: '0.00', lowest: '0.00', totalVoters: '0' };
    }

    const percentages = data
      .map(c => parseFloat(c?.overallPercentage) || 0)
      .filter(p => p > 0);
    
    const voters = data.map(c => parseInt(c?.totalVoters) || 0);

    if (percentages.length === 0) {
      return {
        total: data.length,
        avg: '0.00',
        highest: '0.00',
        lowest: '0.00',
        totalVoters: voters.reduce((a, b) => a + b, 0).toLocaleString()
      };
    }

    return {
      total: data.length,
      avg: (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(2),
      highest: Math.max(...percentages).toFixed(2),
      lowest: Math.min(...percentages).toFixed(2),
      totalVoters: voters.reduce((a, b) => a + b, 0).toLocaleString()
    };
  };

  const summary = getConstituencySummary();

  const getOverallStats = () => {
    const data = Array.isArray(districtStats) ? districtStats : [];
    
    if (data.length === 0) return null;
    
    const percentages = data
      .map(d => parseFloat(d?.averagePercentage) || 0)
      .filter(p => p > 0);
    
    if (percentages.length === 0) return null;
    
    return {
      totalDistricts: data.length,
      avgPercentage: (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(2),
      highest: Math.max(...percentages).toFixed(2),
      lowest: Math.min(...percentages).toFixed(2)
    };
  };

  const overallStats = getOverallStats();

  // ============ RENDER ============
  
  return (
    <>
      <Header />
      <div className="polling-page">
        
        {/* ========== HEADER ========== */}
        <div className="polling-header">
          <div className="header-content">
            <h1>
              <i className="fas fa-chart-pie"></i>
              {t('polling.title')}
            </h1>
            <p className="subtitle">
              {t('polling.subtitle')}
            </p>
          </div>
        </div>

        {/* ========== PHASE SELECTOR ========== */}
        <div className="phase-selector-container">
          <div className="selector-header">
            <label>📅 {t('polling.phaseSelection.selectPhase')}:</label>
            <span className="current-date">
              {t('polling.phaseSelection.phase')} {selectedPhase} - {getPhaseDate(selectedPhase)}
            </span>
          </div>
          <div className="phase-buttons">
            {[1, 2].map(phase => (
              <button
                key={phase}
                className={`phase-btn ${selectedPhase === phase ? 'active' : ''}`}
                onClick={() => setSelectedPhase(phase)}
                disabled={loading}
                title={`${t('polling.phaseSelection.phase')} ${phase} - ${getPhaseDate(phase)}`}
              >
                <span className="phase-num">{phase}</span>
                <span className="phase-label">{t('polling.phaseSelection.phase')}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ========== ERROR ALERT ========== */}
        {error && (
          <div className="error-alert">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
            <button onClick={() => setError(null)} className="close-alert">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* ========== LOADING STATE ========== */}
        {loading && (
          <div className="loading-spinner">
            <div className="spinner-animation"></div>
            <p>{t('polling.messages.loading')}</p>
          </div>
        )}

        {/* ========== CONTROLS (SORT & SEARCH) ========== */}
        {!loading && (
          <div className="controls-section">
            <div className="search-control">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder={selectedDistrict ? t('polling.search.searchConstituency') : t('polling.search.searchDistrict')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="sort-control">
              <label>📊 {t('polling.sort.sortBy')}:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="percentage">{t('polling.sort.overallPercentage')}</option>
                <option value="male">{t('polling.sort.malePercentage')}</option>
                <option value="female">{t('polling.sort.femalePercentage')}</option>
              </select>
            </div>
          </div>
        )}

        {/* ========== DISTRICTS VIEW ========== */}
        {!loading && !selectedDistrict && (
          <div className="districts-section">
            {/* Overall Statistics */}
            {overallStats && (
              <div className="overall-stats">
                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-map"></i></div>
                  <div className="stat-info">
                    <span className="stat-label">{t('polling.statistics.totalDistricts')}</span>
                    <span className="stat-value">{overallStats.totalDistricts}</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
                  <div className="stat-info">
                    <span className="stat-label">{t('polling.statistics.averagePolling')}</span>
                    <span className="stat-value">{overallStats.avgPercentage}%</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-arrow-up"></i></div>
                  <div className="stat-info">
                    <span className="stat-label">{t('polling.statistics.highest')}</span>
                    <span className="stat-value">{overallStats.highest}%</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><i className="fas fa-arrow-down"></i></div>
                  <div className="stat-info">
                    <span className="stat-label">{t('polling.statistics.lowest')}</span>
                    <span className="stat-value">{overallStats.lowest}%</span>
                  </div>
                </div>
              </div>
            )}

            <h2 className="section-title">
              <i className="fas fa-map-marker-alt"></i>
              {t('polling.districtsView.title')}
            </h2>
            
            {sortedDistrictData && sortedDistrictData.length > 0 ? (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="rank-col">#</th>
                      <th>{t('polling.districtsView.district')}</th>
                      <th className="center">{t('polling.districtsView.constituencies')}</th>
                      <th className="center">{t('polling.districtsView.overallPercentage')}</th>
                      <th className="center">{t('polling.districtsView.malePercentage')}</th>
                      <th className="center">{t('polling.districtsView.femalePercentage')}</th>
                      <th>{t('polling.districtsView.topConstituency')}</th>
                      <th className="center">{t('polling.districtsView.voters')}</th>
                      <th className="center">{t('polling.districtsView.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDistrictData.map((item, idx) => (
                      <tr key={item?.district || idx} className="district-row">
                        <td className="rank-col">
                          <span className="rank-badge">
                            {getRankBadge(idx + 1) || <span className="rank-num">{idx + 1}</span>}
                          </span>
                        </td>
                        <td className="district-cell">
                          <div className="district-info">
                            <i className="fas fa-map-pin"></i>
                            <span>{item?.district || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="center">{item?.totalConstituencies || 0}</td>
                        <td className="center">
                          <div className="percentage-cell">
                            <span
                              className="percentage-badge"
                              style={{ backgroundColor: getPercentageColor(item?.averagePercentage) }}
                            >
                              {parseFloat(item?.averagePercentage || 0).toFixed(2)}%
                            </span>
                          </div>
                        </td>
                        <td className="center">
                          <span className="gender-badge male">
                            <i className="fas fa-mars"></i> {parseFloat(item?.averageMalePercentage || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="center">
                          <span className="gender-badge female">
                            <i className="fas fa-venus"></i> {parseFloat(item?.averageFemalePercentage || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="top-constituency">
                          {item?.highestConstituency || 'N/A'}
                        </td>
                        <td className="center">
                          {parseInt(item?.totalVoters || 0).toLocaleString()}
                        </td>
                        <td className="center">
                          <button
                            className="action-btn view-btn"
                            onClick={() => loadConstituencyData(item?.district)}
                            title={t('polling.districtsView.viewDetails')}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>
                  {t('polling.districtsView.noData')}
                  {searchTerm ? ` ${t('polling.messages.matchingSearch')}` : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ========== CONSTITUENCIES VIEW ========== */}
        {!loading && selectedDistrict && (
          <div className="constituencies-section">
            
            {/* Header with Back Button */}
            <div className="constituency-header">
              <button
                className="back-btn"
                onClick={() => {
                  setSelectedDistrict(null);
                  setConstituencyData([]);
                  setSearchTerm('');
                }}
              >
                <i className="fas fa-arrow-left"></i>
                {t('polling.constituenciesView.backToDistricts')}
              </button>
              <h2>
                <i className="fas fa-building"></i>
                {selectedDistrict} - {t('polling.constituenciesView.constituencyDetails')}
              </h2>
            </div>

            {/* Summary Cards */}
            {constituencyData && Array.isArray(constituencyData) && constituencyData.length > 0 && (
              <div className="summary-cards">
                <div className="summary-card total">
                  <i className="fas fa-university"></i>
                  <div>
                    <label>{t('polling.statistics.totalConstituencies')}</label>
                    <value>{summary.total}</value>
                  </div>
                </div>

                <div className="summary-card average">
                  <i className="fas fa-chart-line"></i>
                  <div>
                    <label>{t('polling.statistics.averagePercentage')}</label>
                    <value>{summary.avg}%</value>
                  </div>
                </div>

                <div className="summary-card highest">
                  <i className="fas fa-arrow-up"></i>
                  <div>
                    <label>{t('polling.statistics.highest')}</label>
                    <value>{summary.highest}%</value>
                  </div>
                </div>

                <div className="summary-card lowest">
                  <i className="fas fa-arrow-down"></i>
                  <div>
                    <label>{t('polling.statistics.lowest')}</label>
                    <value>{summary.lowest}%</value>
                  </div>
                </div>

                <div className="summary-card voters">
                  <i className="fas fa-users"></i>
                  <div>
                    <label>{t('polling.statistics.totalVoters')}</label>
                    <value>{summary.totalVoters}</value>
                  </div>
                </div>
              </div>
            )}

            {/* Constituencies Table */}
            {sortedConstituencyData && sortedConstituencyData.length > 0 ? (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="index-col">#</th>
                      <th>{t('polling.constituenciesView.constituency')}</th>
                      <th className="center">{t('polling.districtsView.overallPercentage')}</th>
                      <th className="center">{t('polling.districtsView.malePercentage')}</th>
                      <th className="center">{t('polling.districtsView.femalePercentage')}</th>
                      <th className="center">{t('polling.districtsView.voters')}</th>
                      <th className="center">{t('polling.constituenciesView.progress')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedConstituencyData.map((item, idx) => (
                      <tr key={item?.id || idx} className="constituency-row">
                        <td className="index-col">{idx + 1}</td>
                        <td className="constituency-cell">
                          <div className="constituency-info">
                            <i className="fas fa-location-dot"></i>
                            <span>{item?.constituency || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="center">
                          <span
                            className="percentage-badge"
                            style={{ backgroundColor: getPercentageColor(item?.overallPercentage) }}
                          >
                            {parseFloat(item?.overallPercentage || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="center">
                          <span className="gender-badge male">
                            <i className="fas fa-mars"></i> {parseFloat(item?.malePercentage || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="center">
                          <span className="gender-badge female">
                            <i className="fas fa-venus"></i> {parseFloat(item?.femalePercentage || 0).toFixed(2)}%
                          </span>
                        </td>
                        <td className="center">
                          {parseInt(item?.totalVoters || 0).toLocaleString()}
                        </td>
                        <td className="progress-col">
                          <div className="progress-wrapper">
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${Math.min(parseFloat(item?.overallPercentage || 0), 100)}%`,
                                  backgroundColor: getPercentageColor(item?.overallPercentage)
                                }}
                              />
                            </div>
                            <span className="progress-text">
                              {parseFloat(item?.overallPercentage || 0).toFixed(2)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>
                  {t('polling.constituenciesView.noData')}
                  {searchTerm ? ` ${t('polling.messages.matchingSearch')}` : ''}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PollingPercentage;
